import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent, trackLeadConversion } from "@/lib/analytics";
import { Phone, Mail, Instagram, MessageCircle, Send } from "lucide-react";
import TikTokIcon from "@/components/icons/TikTokIcon";
import SectionHeading from "./SectionHeading";
import { useTr, useLocalizedPath } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  city: "",
  guests: "",
  message: "",
  referrer: "",
  privacy_accepted: false,
  newsletter_optin: false,
};

// Parametri pubblicitari catturati all'arrivo (Google Ads, campagne, referral)
function captureAttribution() {
  try {
    const p = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"];
    const out = { landing_page: window.location.pathname };
    keys.forEach((k) => {
      const v = p.get(k);
      if (v) {
        out[k] = v;
        sessionStorage.setItem(`dj-attr-${k}`, v);
      } else {
        const saved = sessionStorage.getItem(`dj-attr-${k}`);
        if (saved) out[k] = saved;
      }
    });
    return out;
  } catch {
    return { landing_page: window.location.pathname };
  }
}

const inputCls =
  "w-full bg-[#111111] gold-frame rounded-md px-4 py-3 text-base sm:text-sm text-[#F7DE8B] placeholder-[#9F8A52] focus:outline-none transition-colors";

const channels = [
  {
    testid: "contact-phone-call-button",
    icon: Phone,
    label: "Telefono",
    value: "076 441 13 59",
    href: "tel:+41764411359",
  },
  {
    testid: "contact-whatsapp-button",
    icon: MessageCircle,
    label: "WhatsApp",
    value: "+41 76 441 13 59",
    href: "https://wa.me/41764411359",
  },
  {
    testid: "contact-email-link",
    icon: Mail,
    label: "Email",
    value: "info@djmagix.net",
    href: "mailto:info@djmagix.net",
  },
  {
    testid: "contact-instagram-link",
    icon: Instagram,
    label: "Instagram",
    value: "@djmagixx",
    href: "https://www.instagram.com/djmagixx/",
  },
  {
    testid: "contact-tiktok-link",
    icon: TikTokIcon,
    label: "TikTok",
    value: "@dj_magix",
    href: "https://www.tiktok.com/@dj_magix",
  },
];

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const tr = useTr();
  const privacyPath = useLocalizedPath("/privacy");
  const terminiPath = useLocalizedPath("/termini");
  const eventTypes = tr("contact.eventTypes", ["Compleanno", "Festa di Paese", "Matrimonio", "Festa Privata", "Evento Aziendale", "Club / Locale", "Altro"]);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) setForm((f) => ({ ...f, referrer: f.referrer || ref }));
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updateCheck = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) {
      toast.error(tr("contact.privacyError", "Devi accettare la Privacy Policy per inviare la richiesta."));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, ...captureAttribution() });
      toast.success(tr("contact.success", "Richiesta inviata! Ti ricontatterò al più presto."));
      trackLeadConversion();
      setForm(initial);
      navigate("/grazie");
    } catch {
      toast.error(tr("contact.error", "Qualcosa è andato storto. Riprova o chiamami direttamente."));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="py-24 bg-[#080808]">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow={tr("contact.eyebrow", "Contatti")}
          title={tr("contact.title", "Prenota il Tuo DJ Set")}
          accent={tr("contact.accent", "Raccontami il tuo evento: riceverai un preventivo gratuito e senza impegno.")}
          testid="contact-heading"
        />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 px-6">
          <div className="lg:col-span-2 flex flex-col gap-4">
            {channels.map((c) => (
              <a
                key={c.testid}
                data-testid={c.testid}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                onClick={() => {
                  const ev = c.href.startsWith("tel:") ? "click_phone" : c.href.startsWith("mailto:") ? "click_email" : c.href.includes("wa.me") ? "click_whatsapp" : "cta_click";
                  trackEvent(ev, { source: "contact_section" });
                }}
                className="gold-frame rounded-xl bg-[#111111] p-5 flex items-center gap-4 group"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(226,184,87,0.4)] text-[#E2B857] group-hover:bg-[#E2B857]/10 transition-colors">
                  <c.icon size={20} strokeWidth={1.75} />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-[#BFA45D]">
                    {c.label === "Telefono" ? tr("contact.phone", "Telefono") : c.label}
                  </span>
                  <span className="block text-base font-semibold text-[#F7DE8B] mt-0.5">
                    {c.value}
                  </span>
                </span>
              </a>
            ))}
            <p className="text-sm text-[#9F8A52] leading-relaxed mt-2">
              {tr("contact.availability", "Disponibile in tutta la Svizzera: Ticino, Lugano, Zurigo, Ginevra — e su richiesta nel Nord Italia (Como, Milano).")}
            </p>
          </div>

          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-3 gold-frame rounded-2xl bg-[#0D0D0D] p-7 sm:p-9"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                data-testid="contact-form-name-input"
                required
                aria-label={tr("contact.name", "Nome e Cognome")}
                placeholder={tr("contact.name", "Nome e Cognome *")}
                value={form.name}
                onChange={update("name")}
                className={inputCls}
              />
              <input
                data-testid="contact-form-email-input"
                required
                type="email"
                aria-label="Email"
                placeholder="Email *"
                value={form.email}
                onChange={update("email")}
                className={inputCls}
              />
              <input
                data-testid="contact-form-phone-input"
                aria-label={tr("contact.phone", "Telefono")}
                placeholder={tr("contact.phonePh", "Telefono")}
                value={form.phone}
                onChange={update("phone")}
                className={inputCls}
              />
              <select
                data-testid="contact-form-event-type"
                aria-label={tr("contact.eventType", "Tipo di evento")}
                value={form.event_type}
                onChange={update("event_type")}
                className={`${inputCls} ${form.event_type ? "" : "text-[#9F8A52]"}`}
              >
                <option value="">{tr("contact.eventType", "Tipo di evento")}</option>
                {eventTypes.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <input
                data-testid="contact-form-date-input"
                type="date"
                aria-label={tr("contact.dateAria", "Data dell'evento")}
                value={form.event_date}
                onChange={update("event_date")}
                className={inputCls}
              />
              <input
                data-testid="contact-form-city-input"
                aria-label={tr("contact.city", "Città o location")}
                placeholder={tr("contact.city", "Città / Location")}
                value={form.city}
                onChange={update("city")}
                className={inputCls}
              />
              <input
                data-testid="contact-form-guests-input"
                aria-label={tr("contact.guests", "Numero di ospiti circa")}
                placeholder={tr("contact.guests", "Numero ospiti (circa)")}
                value={form.guests}
                onChange={update("guests")}
                className={inputCls}
              />
              <textarea
                data-testid="contact-form-message-input"
                aria-label={tr("contact.message", "Messaggio")}
                placeholder={tr("contact.message", "Raccontami il tuo evento...")}
                rows={4}
                value={form.message}
                onChange={update("message")}
                className={`${inputCls} sm:col-span-2 resize-none`}
              />
              <input
                data-testid="contact-form-referrer-input"
                aria-label={tr("contact.referrer", "Chi ti ha segnalato")}
                placeholder={tr("contact.referrer", "Chi ti ha segnalato? (facoltativo)")}
                value={form.referrer}
                onChange={update("referrer")}
                className={`${inputCls} sm:col-span-2`}
              />
            </div>
            <div className="mt-5 space-y-3">
              <label
                className="flex items-start gap-3 cursor-pointer group"
                data-testid="contact-form-privacy-label"
              >
                <input
                  type="checkbox"
                  data-testid="contact-form-privacy-checkbox"
                  checked={form.privacy_accepted}
                  onChange={updateCheck("privacy_accepted")}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#E2B857] cursor-pointer"
                />
                <span className="text-xs text-[#9F8A52] leading-relaxed group-hover:text-[#E2C882] transition-colors">
                  {tr("contact.privacyPre", "Ho letto e accetto la")}{" "}
                  <a href={privacyPath} target="_blank" rel="noopener" className="text-[#E2B857] underline underline-offset-2">
                    Privacy Policy
                  </a>{" "}
                  {tr("contact.privacyAnd", "e i")}{" "}
                  <a href={terminiPath} target="_blank" rel="noopener" className="text-[#E2B857] underline underline-offset-2">
                    {tr("contact.terms", "Termini di Servizio")}
                  </a>{" "}
                  *
                </span>
              </label>
              <label
                className="flex items-start gap-3 cursor-pointer group"
                data-testid="contact-form-newsletter-label"
              >
                <input
                  type="checkbox"
                  data-testid="contact-form-newsletter-checkbox"
                  checked={form.newsletter_optin}
                  onChange={updateCheck("newsletter_optin")}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#E2B857] cursor-pointer"
                />
                <span className="text-xs text-[#9F8A52] leading-relaxed group-hover:text-[#E2C882] transition-colors">
                  {tr("contact.newsletter", "Voglio ricevere novità e offerte via email (facoltativo — cancellazione con un click)")}
                </span>
              </label>
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="gold-btn rounded-full w-full mt-6 px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Send size={16} /> {sending ? tr("contact.sending", "Invio in corso...") : tr("contact.submit", "Invia Richiesta")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
